import getCoverageQuestionsApi from 'api/coverageQuestion';
import createQuestionApi, {
  CreateQuestionBody,
} from 'api/createQuestion/createQuestion';
import { push } from 'connected-react-router';
import { call, put, select } from 'redux-saga/effects';
import appActions from 'redux/actions/applicationSpinnerActions';
import mobilityContinuationActions from 'redux/actions/mobilityContinuationActions';
import { caseGuidCreatedSelector } from 'redux/selectors/caseCreation';
import {
  MCSupportedServiceKeysSelector,
  partnerSelector,
  selectTenantId,
  shouldRedirectToCallbackIfServicesRejectedSelector,
  selectProvider,
  shouldCreateMCServicesOnlyForFilteredSelector,
} from 'redux/selectors/config';
import {
  mobilityContinuationProductIdSelector,
  answersSelector,
  MCSelectedLocationSelector,
} from 'redux/selectors/mobilityContinuation';
import { routes } from 'Routes/routesConfig';
import type {
  Answer,
  AnswerWithoutExtraData,
  GetCoverageQuestionsApiBody,
  CoverageQuestionsState,
} from 'ts/types/coverage-questions.types';
import { getSynergySessionToken } from 'utils/sagas-utils';
import type { RemoveLastAnswerReturn } from './types';
import {
  getCoveredServicesByCriteria,
  isAnswerRemovable,
  removeExtraDataFromAnswers,
  removeLastAnswerFromCoverage,
} from './utils';

type GetBodyForCreateQuestionParams = {
  answers: AnswerWithoutExtraData[];
  addresses?: {
    origin?: any;
    destination?: any;
  };
};

type GetBodyForCreateQuestion = (
  params: GetBodyForCreateQuestionParams,
) => CreateQuestionBody;

export const getBodyForCreateQuestion: GetBodyForCreateQuestion = ({
  addresses,
  answers,
}) => ({
  preferred_touch_point: 'SMS',
  service_context: {
    questions_and_answers: answers,
    pickup_location: addresses?.origin,
    destination: addresses?.destination,
  },
});

const handleError = (ok: boolean, status: number, result: any) => {
  if (!ok || status !== 200) {
    throw new Error(result);
  }
};

const getAllServicesNotCovered = (
  servicesCovered: any[],
  servicesUnresolved: any[],
) => servicesCovered.length === 0 && servicesUnresolved.length === 0;

function* createQuestionOrThrowError(
  params: {
    provider: string;
    partner: string;
    tenantId: string;
    token: string;
  },
  answers: AnswerWithoutExtraData[] = [],
) {
  const addresses: ReturnType<typeof MCSelectedLocationSelector> = yield select(
    MCSelectedLocationSelector,
  );

  const createQuestionBody = getBodyForCreateQuestion({
    addresses,
    answers,
  });

  const { ok, status, result } = yield call(createQuestionApi, {
    body: createQuestionBody,
    ...params,
  });
  handleError(ok, status, result);
}

export function* getCoverageQuestions() {
  try {
    yield put(appActions.startLoad({}));
    const tenantId: string = yield select(selectTenantId);
    const caseGuid: string = yield select(caseGuidCreatedSelector);
    const token: string = yield getSynergySessionToken();
    const answers: Answer[] = yield select(answersSelector);
    const provider: string = yield select(selectProvider);
    const partner: string = yield select(partnerSelector);
    const productId: string = yield select(
      mobilityContinuationProductIdSelector,
    );
    const shouldRedirectToCallback: boolean = yield select(
      shouldRedirectToCallbackIfServicesRejectedSelector,
    );
    const supportedServices: ReturnType<typeof MCSupportedServiceKeysSelector> =
      yield select(MCSupportedServiceKeysSelector);
    const shouldCreateOnlyFiltered: boolean = yield select(
      shouldCreateMCServicesOnlyForFilteredSelector,
    );

    const answersParsed = removeExtraDataFromAnswers(answers);
    const body: GetCoverageQuestionsApiBody = {
      case_guid: caseGuid,
      productId,
      state: answersParsed || [],
    };
    const { result, status, ok } = yield call(getCoverageQuestionsApi, {
      tenantId,
      body,
      token,
      partner,
    });

    handleError(ok, status, result);

    const servicesCovered = getCoveredServicesByCriteria(
      result.coverageResponses,
      ['COVERED'],
      shouldCreateOnlyFiltered ? supportedServices : [],
    );
    const servicesUnresolved = getCoveredServicesByCriteria(
      result.coverageResponses,
      ['UNRESOLVED'],
      [],
    );

    const areAllServicesNotCovered = getAllServicesNotCovered(
      servicesCovered,
      servicesUnresolved,
    );

    if (areAllServicesNotCovered && shouldCreateOnlyFiltered) {
      yield createQuestionOrThrowError(
        {
          provider,
          partner,
          tenantId,
          token,
        },
        answersParsed,
      );
    }

    if (areAllServicesNotCovered && shouldRedirectToCallback) {
      yield put(push(routes.MOBILITY_CONTINUATION_CALLBACK));
      return;
    }

    if (areAllServicesNotCovered) {
      throw new Error('No services to cover');
    }

    if (servicesCovered.length > 0) {
      yield put(
        mobilityContinuationActions.setCoverageQuestionsData({
          answers,
          data: result,
          coverage: servicesCovered,
        }),
      );
      yield put(push(routes.MOBILITY_CONTINUATION_SERVICES));
      return;
    }

    if (!result.nextQuestion) {
      yield put(push(routes.DROPOFF));
      return;
    }

    const wasFirstQuestionAsked = isAnswerRemovable(result.coverageResponses);
    yield put(
      mobilityContinuationActions.setCoverageQuestionsData({
        answers,
        data: result,
        wasAnswerRemoved: wasFirstQuestionAsked,
      }),
    );
  } catch (error) {
    console.error('Error while fetching coverage questions', error);
    yield put(push(routes.DROPOFF));
  } finally {
    yield put(appActions.stopLoad({}));
  }
}

export function* setCoverageAnswer(action: {
  type: string;
  payload: CoverageQuestionsState['answers'];
}) {
  try {
    const data = action.payload;
    yield put(mobilityContinuationActions.setCoverageResponse(data));
    yield put(mobilityContinuationActions.getCoverageQuestions());
  } catch (error) {
    console.error("Error while setting user's coverage answer", error);
    yield put(push(routes.DROPOFF));
  }
}

export function* goToPreviousQuestion() {
  try {
    const answers: Answer[] = yield select(answersSelector);
    const data: RemoveLastAnswerReturn = yield removeLastAnswerFromCoverage(
      answers,
    );

    if (!data.wasAnswerRemoved) {
      yield put(push(routes.MOBILITY_CONTINUITY_LOCATION));
      return;
    }
    yield put(mobilityContinuationActions.setCoverageQuestionsData(data));
    yield put(mobilityContinuationActions.getCoverageQuestions());
  } catch (error) {
    console.log('Error while removing last answered question', error);
    yield put(push(routes.DROPOFF));
  }
}
